import React from 'react';
import { DAYS, DAY_NAMES, WEEK_MINUTES, windowIntervals, intervalText } from './bonusWindows';

// BONUS-ADMIN-EINFACH — Wochenansicht 7 Tage × 24 Stunden (Berlin). Ein Fenster über Mitternacht
// erscheint als zwei Teile (z. B. Mi 23:00–24:00 und Do 00:00–04:00) und bleibt als EIN Eintrag lesbar.
const COLORS = { draft: '#6d28d9', on: '#0f766e', off: '#64748b', conflict: '#b91c1c' };

function daySegments(intervals) {
  // Wochenintervalle in Tagesstücke [tag, start, ende] zerlegen.
  const out = [];
  for (const [start, end] of intervals) {
    let s = start;
    while (s < end) {
      const day = Math.floor(s / 1440), dayEnd = Math.min(end, (day + 1) * 1440);
      out.push([day % 7, s - day * 1440, dayEnd - day * 1440]);
      s = dayEnd;
    }
  }
  return out;
}

export default function BonusWeekTimeline({ draft = null, offers = [], conflicts = [] }) {
  const layers = [
    ...offers.filter((o) => o.terms?.per_ride_cents > 0).map((o) => ({ key: `offer-${o.id}`, title: o.terms.title,
      kind: o.enabled ? 'on' : 'off', intervals: (o.terms.windows || []).flatMap(windowIntervals) })),
    ...(draft ? [{ key: 'draft', title: draft.title || 'Neues Angebot', kind: 'draft', intervals: draft.windows.flatMap(windowIntervals) }] : []),
  ];
  const lanes = Math.max(layers.length, 1);
  const conflictIntervals = conflicts.flatMap((c) => c.intervals);
  const pct = (m) => `${(m / 1440) * 100}%`;
  return <figure aria-label="Wochenansicht der Zeitfenster" style={{ margin: '12px 0', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
    <figcaption style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Wochenansicht (Berlin) · Mo 00:00 bis So 24:00</figcaption>
    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '4px 8px', alignItems: 'center' }}>
      <span />
      <div style={{ position: 'relative', height: 14, fontSize: 11, color: '#4b5563' }}>
        {[0, 6, 12, 18, 24].map((h) => <span key={h} style={{ position: 'absolute', left: `${(h / 24) * 100}%`, transform: h === 24 ? 'translateX(-100%)' : h ? 'translateX(-50%)' : 'none' }}>{h}</span>)}
      </div>
      {DAYS.map((name, day) => {
        const parts = layers.map((layer, lane) => ({ layer, lane, segments: daySegments(layer.intervals).filter((s) => s[0] === day) }));
        const hits = daySegments(conflictIntervals).filter((s) => s[0] === day);
        return <React.Fragment key={name}>
          <span title={DAY_NAMES[day]} style={{ fontSize: 12, fontWeight: 700 }}>{name}</span>
          <div data-day={day + 1} style={{ position: 'relative', height: lanes * 10 + 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4,
            backgroundImage: 'linear-gradient(to right, #e5e7eb 1px, transparent 1px)', backgroundSize: `${100 / 4}% 100%` }}>
            {parts.flatMap(({ layer, lane, segments }) => segments.map(([, s, e], index) => <span key={`${layer.key}-${index}`} data-kind={layer.kind}
              title={`${layer.title}: ${DAYS[day]} ${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}–${e === 1440 ? '24:00' : `${String(Math.floor(e / 60)).padStart(2, '0')}:${String(e % 60).padStart(2, '0')}`}`}
              style={{ position: 'absolute', left: pct(s), width: pct(e - s), top: 4 + lane * 10, height: 8, borderRadius: 3,
                background: COLORS[layer.kind], opacity: layer.kind === 'off' ? 0.55 : 1 }} />))}
            {hits.map(([, s, e], index) => <span key={`hit-${index}`} data-kind="conflict" style={{ position: 'absolute', left: pct(s), width: pct(e - s), top: 0, bottom: 0,
              border: `2px solid ${COLORS.conflict}`, borderRadius: 4, background: 'rgba(185,28,28,0.12)' }} />)}
          </div>
        </React.Fragment>;
      })}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, fontSize: 12, color: '#374151' }}>
      {draft && <span><span style={{ display: 'inline-block', width: 12, height: 8, background: COLORS.draft, borderRadius: 2, marginRight: 4 }} />Neues Angebot</span>}
      <span><span style={{ display: 'inline-block', width: 12, height: 8, background: COLORS.on, borderRadius: 2, marginRight: 4 }} />Eingeschaltet</span>
      <span><span style={{ display: 'inline-block', width: 12, height: 8, background: COLORS.off, opacity: 0.55, borderRadius: 2, marginRight: 4 }} />Ausgeschaltet</span>
      {!!conflictIntervals.length && <span style={{ color: COLORS.conflict, fontWeight: 700 }}>Rot umrandet: Überschneidung</span>}
    </div>
    {!layers.some((l) => l.intervals.length) && <p style={{ fontSize: 12, margin: '8px 0 0' }}>Noch keine Zeitfenster. Wochenziele gelten die ganze VOOGO-Woche.</p>}
    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
      {layers.filter((l) => l.intervals.length).map((l) => <li key={l.key}>{l.title} ({l.kind === 'draft' ? 'neu' : l.kind === 'on' ? 'eingeschaltet' : 'ausgeschaltet'}): {mergeText(l.intervals)}</li>)}
    </ul>
  </figure>;
}

function mergeText(intervals) {
  // Ein Fenster So → Mo wird in der Liste wieder als ein Stück gezeigt.
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  if (sorted.length > 1 && sorted[0][0] === 0 && sorted[sorted.length - 1][1] === WEEK_MINUTES) {
    const first = sorted.shift(), last = sorted.pop();
    sorted.push([last[0], WEEK_MINUTES + first[1]]);
  }
  return sorted.map(intervalText).join(', ');
}
